import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../api/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      // In a real app, you might want to validate the token with an API call
      setToken(token);
      // Set user data based on token or a separate API call
      // Check if we have stored user info in localStorage
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        try {
          const userData = JSON.parse(storedUserData);
          setUser(userData);
        } catch (e) {
          // Fallback to email/username if JSON parsing fails
          const storedUsername = localStorage.getItem('username');
          const storedEmail = localStorage.getItem('userEmail');
          setUser({ 
            email: storedEmail || 'user',
            username: storedUsername || storedEmail?.split('@')[0] || 'user'
          });
        }
      } else {
        const storedUsername = localStorage.getItem('username');
        const storedEmail = localStorage.getItem('userEmail');
        setUser({ 
          email: storedEmail || 'user',
          username: storedUsername || storedEmail?.split('@')[0] || 'user'
        });
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    // Clear any existing token before logging in with a different user
    localStorage.removeItem('token');
    
    try {
      const response = await authAPI.login(username, password);
      // The new API returns a full user object with tokens
      const userData = response.data; 
      
      // Extract token information from the response
      const accessToken = userData.access_token;
      
      if (accessToken) {
        localStorage.setItem('token', accessToken);
        setToken(accessToken);
      }
      
      // Store user data in localStorage
      localStorage.setItem('userData', JSON.stringify({
        id: userData.id,
        email: userData.email,
        username: userData.username,
        emp_id: userData.emp_id,
        name: userData.name,
        organization_id: userData.organization_id
      }));
      localStorage.setItem('userEmail', userData.email);
      if (userData.username) {
        localStorage.setItem('username', userData.username);
      }
      
      // Set user state
      setUser({
        id: userData.id,
        email: userData.email,
        username: userData.username,
        emp_id: userData.emp_id,
        name: userData.name,
        organization_id: userData.organization_id
      });
      
      return { success: true, user: userData };
    } catch (error) {
      // Handle different error response formats
      let errorMessage = 'Login failed';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.errors) {
        // External auth error format
        errorMessage = typeof error.response.data.errors === 'string' 
          ? error.response.data.errors 
          : JSON.stringify(error.response.data.errors);
      } else if (error.response?.data && Array.isArray(error.response.data)) {
        errorMessage = error.response.data.map(err => err.msg).join(', ');
      } else if (typeof error.response?.data === 'object' && error.response.data.msg) {
        errorMessage = error.response.data.msg;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const register = async (userData) => {
    try {
      // Separate organization data from user data
      const { organizationName, organizationLocation, ...userOnlyData } = userData;
      
      // Create user with organization data
      const response = await authAPI.register({
        ...userOnlyData,
        organization_name: organizationName,
        organization_location: organizationLocation
      });
      
      return { success: true, user: response.data };
    } catch (error) {
      // Handle different error response formats
      let errorMessage = 'Registration failed';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data && Array.isArray(error.response.data)) {
        errorMessage = error.response.data.map(err => err.msg).join(', ');
      } else if (typeof error.response?.data === 'object' && error.response.data.msg) {
        errorMessage = error.response.data.msg;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('username');
    localStorage.removeItem('userData');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};